/**
 * Utils - Singleton collection of utility functions for data manipulation
 *
 * SINGLETON: Provides ~100+ utility functions, primarily wrapping lodash for framework
 * integration. All functions are static and available globally once loaded.
 *
 * PUBLIC API NOTE: Most methods are static (no context injection). Non-static methods
 * auto-inject caller context for iterator binding. JSDoc @param lists show only
 * user-provided parameters.
 *
 * KEY PATTERN: When called via instance (this.each, this.map, etc.), iterator functions
 * are automatically bound to instance context, eliminating need for manual .bind(this).
 *
 * ARCHITECTURE: Most Utils functions are pass-throughs to lodash with framework-specific
 * enhancements. See {@link https://lodash.com/docs|lodash documentation} for detailed
 * behavior of underlying functions.
 *
 * Categories:
 * - Collection utilities (each, map, filter, reduce, find)
 * - Type checking (isArray, isFunction, isString, etc.)
 * - Object manipulation (assign, merge, clone, defaults)
 * - Function utilities (bind, debounce, throttle)
 * - Array utilities (chunk, flatten, unique, sort)
 * - String utilities (trim, capitalize, camelCase)
 * - Number utilities (random, clamp, toInteger)
 * - Promise utilities (delay, defer)
 *
 * @class Utils
 * @extends Ancestor
 * @singleton
 *
 * @example
 * // Access via dependency injection
 * requires: ['Utils']
 * // Then use as this.Utils.methodName()
 *
 * @example
 * // Auto-bound context in iterators (from instance)
 * this.each(items, function(item) {
 *     this.doSomething(item);  // 'this' is instance, no .bind needed!
 * });
 *
 * @example
 * // Static usage (from singleton)
 * const mapped = Utils.map(items, 'propertyName');  // Pluck property
 */
define("Utils", [`Ancestor`, `module`, `libphonenumberjs`, `lodash`], function (Super, Self, libphonenumberjs, _) {
	// Capture lodash type checks at module init so Utils.isString never calls back into itself
	// if lodash is patched later (e.g. aliased to Utils helpers).
	const lodashIsString = _.isString;
	const lodashIsUndefined = _.isUndefined;

	return Super.inherit({
		namespace: Self.id,
		singleton: true,
		api: {
			/**
			 * Assign values to a destination object, based on one or more passed maps of source values.
			 * @method Utils.assign
			 * @param {Object} destination - The target object that new properties will be defined on
			 * @param {...Object} sources - One or more objects whose properties will be copied to the destination object
			 * @returns {Object} The object passed in as the destination param, augmented with any source properties
			 */
			assign: {
				static: true,
				public: true,
				value(target) {
					const args = _.toArray(arguments);

					if (_.isUndefined(target)) {
						args[0] = {};
					}

					return _.assign.apply(undefined, args);
				},
			},

			/**
			 * Bind function to context (with optional context override).
			 *
			 * @method Utils#bind
			 * @param {Function|Function[]} fn - Function(s) to bind
			 * @param {*} [customContext] - Override context (defaults to caller instance)
			 * @returns {Function|Function[]} Bound function(s)
			 */
			bind: {
				public: true,
				value(context, fn, customContext) {
					if (!_.isUndefined(customContext)) {
						context = customContext;
					}

					if (_.isFunction(fn)) {
						return _.bind(fn, context);
					} else {
						_.each(fn, function (actualFn, i) {
							fn[i] = _.bind(actualFn, context);
						});

						return fn;
					}
				},
			},

			chain: {
				public: true,
				value(context, collection, customContext) {
					var api;

					if (this.isArguments(collection)) {
						collection = this.toArray(collection);
					}

					if (!this.isUndefined(customContext)) {
						context = customContext;
					}

					api = {
						// chainable functions for all internal values
						value: function () {
							return collection;
						},

						clone: function () {
							var result = this.clone(collection);
							return this.chain(result, context);
						},
						countBy: function (iterator) {
							var result = this.countBy(collection, iterator, context);
							return this.chain(result, context);
						},
						isArray: function (minLength) {
							return this.isArray(collection, minLength);
						},
						isBoolean: function () {
							return this.isBoolean(collection);
						},
						isEmpty: function () {
							return this.isEmpty(collection);
						},
						isFinite: function (min) {
							return this.isFinite(collection, min);
						},
						isFunction: function () {
							return this.isFunction(collection);
						},
						isInt: function (min) {
							return this.isInt(collection, min);
						},
						isNull: function () {
							return this.isNull(collection);
						},
						isObject: function () {
							return this.isObject(collection);
						},
						isString: function (minLength) {
							return this.isString(collection, minLength);
						},
						tap: function (fn) {
							// call a passed function with the current item
							var result = _.clone(collection);
							fn.call(context, result);
							return this.chain(result, context);
						},
						when: function (options) {
							options = this.defaults(options, {
								owner: context,
							});

							options.promise = collection;
							return this.when(options);
						},
						whenAll: function (options) {
							options = this.defaults(options, {
								owner: context,
							});

							options.promises = collection;
							return this.when(options);
						},
					};

					if (this.isObject(collection)) {
						// 'collection' handling, arrays and objects, that involve iterators
						this.assign(api, {
							assign: function (properties) {
								// start with an empty object, instead of modifying the passed one
								var assigned = this.assign({}, collection, properties);
								return this.chain(assigned, context);
							},
							each: function (iterator) {
								this.each(collection, iterator, context);
								return this.chain(collection, context);
							},
							extend: () => {
								throw new Error(`Error: Utils.extend has been replaced:\n
								Use collection.assign(target<Object>, values<Object>[, ...]) instead.`);
							},
							filter: function (iterator) {
								var result = this.filter(collection, iterator, context);
								return this.chain(result, context);
							},
							find: function (iterator) {
								var matching = this.find(collection, iterator, context);
								return this.chain(matching, context);
							},
							flatten: function (shallow) {
								var flattened = this.flatten(collection, shallow);
								return this.chain(flattened, context);
							},
							groupBy: function (iterator) {
								var result = this.groupBy(collection, iterator, context);
								return this.chain(result, context);
							},
							indexBy: function (iterator) {
								var result = this.indexBy(collection, iterator, context);
								return this.chain(result, context);
							},
							keys: function () {
								var keys = this.keys(collection);
								return this.chain(keys, context);
							},
							map: function (iterator) {
								var result = this.map(collection, iterator, context);
								return this.chain(result, context);
							},
							pluck: () => {
								throw new Error(`Error: Utils.pluck has been replaced:\n
								Use collection.map(propName<String>) shorthand instead.`);
							},
							reduce: function (iterator, memo) {
								var result = this.reduce(collection, iterator, memo, context);
								return this.chain(result, context);
							},
							sample: function (number) {
								var result = this.sample(collection, number);
								return this.chain(result, context);
							},
							sortBy: function (iterator) {
								var result = this.sortBy(collection, iterator, context);
								return this.chain(result, context);
							},
						});

						if (!this.isArray(collection)) {
							// Object-specific abilities
							this.assign(api, {
								omit: function () {
									var args = arguments,
										result;

									args.unshift(collection);
									result = this.omit.apply(undefined, args);
									return this.chain(result, context);
								},
							});
						}
					}

					if (this.isArray(collection)) {
						// array-specific functions
						this.assign(api, {
							chunk: function (size) {
								var result = this.chunk(collection, size);
								return this.chain(result, context);
							},
							concat: function () {
								var args = this.toArray(arguments),
									concatenated = Array.prototype.concat.apply(collection, args);

								return this.chain(concatenated, context);
							},
							difference: function () {
								var args = this.toArray(arguments),
									result;

								args.unshift(collection);
								// NOTE: need to call manually, for an arbitrary number of arguments
								result = this.difference.apply(this, args);
								return this.chain(result, context);
							},
							extract: function (properties) {
								var result = this.extract(collection, properties);
								return this.chain(result, context);
							},
							extractWhere: function (properties) {
								var result = this.extractWhere(collection, properties);
								return this.chain(result, context);
							},
							first: function () {
								var result = this.first(collection);
								return this.chain(result, context);
							},
							last: function () {
								var result = this.last(collection);
								return this.chain(result, context);
							},
							initial: function (index) {
								var result = this.initial(collection);
								return this.chain(result, context);
							},
							intersection: function () {
								var args = this.toArray(arguments),
									result;

								args.unshift(collection);
								// NOTE: need to call manually, for an arbitrary number of arguments
								result = this.intersection.apply(this, args);
								return this.chain(result, context);
							},
							pop: function () {
								var result = _.clone(collection);
								result.pop();
								return this.chain(result, context);
							},
							push: function () {
								var result = _.clone(collection);
								Array.prototype.push.apply(result, arguments);
								return this.chain(result, context);
							},
							rest: function (index) {
								var result = this.rest(collection, index);
								return this.chain(result, context);
							},
							reverse: function () {
								var result = _.clone(collection);
								result.reverse();
								return this.chain(result, context);
							},
							shift: function () {
								var result = _.clone(collection);
								Array.prototype.shift.apply(result, arguments);
								return this.chain(result, context);
							},
							slice: function (begin, end) {
								var result = collection.slice(begin, end);
								return this.chain(result, context);
							},
							sort: function () {
								var result = _.clone(collection).sort();
								return this.chain(result, context);
							},
							splice: function () {
								var result = _.clone(collection);
								Array.prototype.splice.apply(result, arguments);
								return this.chain(result, context);
							},
							union: function () {
								var args = this.toArray(arguments),
									result;

								args.unshift(collection);
								// NOTE: need to call manually, for an arbitrary number of arguments
								result = this.union.apply(this, args);
								return this.chain(result, context);
							},
							unique: function () {
								var result = this.unique(collection);
								return this.chain(result, context);
							},
							unshift: function () {
								var result = _.clone(collection);
								Array.prototype.unshift.apply(result, arguments);
								return this.chain(result, context);
							},
							without: function () {
								var args = this.toArray(arguments),
									result;

								args.unshift(collection);
								// NOTE: need to call manually, for an arbitrary number of arguments
								result = this.without.apply(this, args);
								return this.chain(result, context);
							},
						});
					}

					return this.reduce(
						api,
						function (chainable, value, key) {
							// iterate over the API
							Object.defineProperty(chainable, key, {
								value: this.bind(value),
								enumerable: false,
							});

							return chainable;
						},
						{},
						this
					); // start with an empty object
				},
			},

			formatPhoneNumber: {
				public: true,
				value(context, phoneNumber, format) {
					const { parsePhoneNumber } = libphonenumberjs;
					const defaultCountry = `US`;

					// support for { countryPrefix, phoneNumber } objects, in addition to string as phoneNumber
					if (
						this.isObject(phoneNumber) &&
						this.isString(phoneNumber.phoneNumber, 1) &&
						this.isString(phoneNumber.countryPrefix, 1)
					) {
						// if phoneNumber includes countryPrefix, avoid displaying countryPrefix twice (backwards compatibility with some existing phone numbers)
						phoneNumber = phoneNumber.phoneNumber.includes(`+`)
							? phoneNumber.phoneNumber
							: `${phoneNumber.countryPrefix} ${phoneNumber.phoneNumber}`;
					} else if (this.isObject(phoneNumber) && this.isString(phoneNumber.phoneNumber, 1)) {
						// template util passes object
						format = phoneNumber.format;
						phoneNumber = phoneNumber.phoneNumber;
					}

					try {
						if (this.isString(phoneNumber, 1) && /[a-zA-Z]/.test(phoneNumber)) {
							// vanity numbers (eg. "833.GO.BAYFC") mix letters with digits and can't be
							// parsed/formatted by libphonenumber, so normalize separators and display
							// them with the same "+1 " country code prefix as other US numbers
							const normalized = phoneNumber.trim().replace(/[\s.-]+/g, ` `);

							return normalized.startsWith(`+`) ? normalized : `+1 ${normalized}`;
						}

						const phoneNumberObj = parsePhoneNumber(phoneNumber, { defaultCountry });

						if (!this.isString(format, 1)) {
							format = phoneNumberObj.country === `US` ? `NATIONAL` : `INTERNATIONAL`;
						}

						return phoneNumberObj.country === `US` && format === `NATIONAL`
							? `+1 ${phoneNumberObj.format(format)}`
							: phoneNumberObj.format(format);
					} catch (error) {
						return this.isObject(phoneNumber) ? null : phoneNumber;
					}
				},
			},

			parsePhoneNumber: {
				public: true,
				value(context, phoneNumber) {
					const { parsePhoneNumber } = libphonenumberjs;
					const defaultCountry = `US`;

					try {
						const { countryCallingCode, nationalNumber } = parsePhoneNumber(phoneNumber, { defaultCountry });

						return { countryPrefix: `+${countryCallingCode}`, phoneNumber: nationalNumber };
					} catch (error) {
						return null;
					}
				},
			},

			/**
			 * Used for splitting an array into a 'paginated' list of arrays.
			 * @method Utils.chunk
			 * @param {Array} collection -
			 * @param {number} size -
			 * @returns {Array[]} The newly created, permanently bound version of the passed function
			 * @example
			 * this.chunk([1,2,3,4,5], 2); // returns [[1,2], [3,4], [5]]
			 */
			chunk: {
				static: true,
				public: true,
				value: _.chunk,
			},

			/**
			 * Converts a color string to a valid rgba() CSS value.
			 * Accepts hex strings ('#RRGGBB' or '#RGB') or RGB component strings ('R, G, B').
			 * @method Utils.colorToRgba
			 * @param {string} color - Hex ('#RRGGBB'/'#RGB') or RGB components ('R, G, B')
			 * @param {number} [opacity=1] - Alpha value between 0 and 1
			 * @returns {string} A valid rgba() CSS value
			 */
			colorToRgba: {
				static: true,
				public: true,
				value(color, opacity = 1) {
					const alpha = Math.max(0, Math.min(1, opacity));

					if (/^#([0-9a-fA-F]{3}){1,2}$/.test(color)) {
						let hex = color.slice(1);

						if (hex.length === 3) {
							hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
						}

						const r = parseInt(hex.slice(0, 2), 16);
						const g = parseInt(hex.slice(2, 4), 16);
						const b = parseInt(hex.slice(4, 6), 16);

						return `rgba(${r}, ${g}, ${b}, ${alpha})`;
					}

					// assume 'R, G, B' component string
					return `rgba(${color}, ${alpha})`;
				},
			},

			contains: {
				static: true,
				public: true,
				value() {
					throw new Error(`Error: Utils.contains has been replaced:\n
					Use Utils.includes(collection, value) instead.`);
				},
			},

			/**
			 * @method Utils.countBy
			 */
			countBy: {
				public: true,
				value(context, collection, iterator, customContext) {
					if (!_.isUndefined(customContext)) {
						context = customContext;
					}

					if (_.isFunction(iterator)) {
						iterator = iterator.bind(context);
					}

					return _.countBy(collection, iterator, context);
				},
			},

			/**
			 * @method Utils.defaults
			 */
			defaults: {
				public: true,
				static: true,
				value(target) {
					var args = _.toArray(arguments);
					if (_.isUndefined(target)) {
						args[0] = {};
					}

					return _.defaults.apply(undefined, args);
				},
			},

			/**
			 * @method Utils.debounce
			 * @see lodash.debounce
			 */
			debounce: {
				static: true,
				public: true,
				value(fn, wait, _options) {
					const options = {};

					// backwards compatibility for debounceImmediate option from underscore
					if (_.isBoolean(_options) && _options === true) {
						options.leading = true;
						options.trailing = false;
					}

					return _.debounce(fn, wait, options);
				},
			},

			/**
			 * @method Utils.difference
			 * @see lodash.difference
			 */
			difference: {
				static: true,
				public: true,
				value: _.difference,
			},

			/**
			 * @method Utils.each
			 * @see lodash.each
			 */
			each: {
				public: true,
				value(context, collection, iterator, customContext) {
					if (!_.isUndefined(customContext)) {
						context = customContext;
					}

					if (_.isFunction(iterator)) {
						iterator = iterator.bind(context);
					}

					return _.each(collection, iterator);
				},
			},

			/**
			 * @method Utils.every
			 * @see lodash.every
			 */
			every: {
				public: true,
				value(context, collection, iterator, customContext) {
					if (!_.isUndefined(customContext)) {
						context = customContext;
					}

					if (_.isFunction(iterator)) {
						iterator = iterator.bind(context);
					}

					return _.every(collection, iterator);
				},
			},
			/**
			 * @method Utils.extend
			 * @see lodash.extend
			 */
			extend: {
				public: true,
				static: true,
				value() {
					throw new Error(`Error: Utils.extend has been replaced:\n
					Use Utils.assign(object, properties<Object>, ...).`);
				},
			},

			/**
			 * @method Utils.extract
			 */
			extract: {
				public: true,
				value(context, collection, properties) {
					let validator;
					let result;

					if (_.isFunction(properties)) {
						validator = (item) => properties.call(context, item);
					} else if (_.isObject(properties)) {
						// create a function to check against the property map
						validator = (item) => {
							let result = true;

							_.find(properties, (value, key) => {
								if (item[key] !== value) {
									result = false;
									return true; // break loop
								}
							});

							return result;
						};
					}

					if (_.isArray(collection)) {
						result = [];

						for (var i = 0; i < collection.length; i++) {
							const item = collection[i];
							const doExtract = validator(item);

							if (doExtract) {
								const spliced = collection.splice(i, 1);
								const [extractedItem] = spliced;

								result.push(extractedItem);
								i--; // spliced away an item, repeat this index
							}
						}
					} else {
						// assume object
						result = {};

						for (const key in collection) {
							const item = collection[key];
							const doExtract = validator(context, item);

							if (doExtract) {
								delete collection[key];
								result[key] = item;
							}
						}
					}

					return result;
				},
			},

			/**
			 * @method Utils.extract
			 */
			extractWhere: {
				public: true,
				value(context, collection, properties) {
					let validator;
					let result;

					if (_.isFunction(properties)) {
						validator = (item) => properties.call(context, item);
					} else if (_.isObject(properties)) {
						// create a function to check against the property map
						validator = (item) => {
							let result = true;

							_.find(properties, (value, key) => {
								if (item[key] !== value) {
									result = false;
									return true; // break loop
								}
							});

							return result;
						};
					}

					if (_.isArray(collection)) {
						result = [];

						for (var i = 0; i < collection.length; i++) {
							const item = collection[i];
							const doExtract = validator(item);

							if (doExtract) {
								const spliced = collection.splice(i, 1);
								const [extractedItem] = spliced;

								return extractedItem;
							}
						}
					} else {
						// assume object
						for (const key in collection) {
							const item = collection[key];
							const doExtract = validator(context, item);

							if (doExtract) {
								delete collection[key];
								return result;
							}
						}
					} // return undefinedd if no match
				},
			},

			/**
			 * @method Utils.filter
			 * @see lodash.filter
			 */
			filter: {
				public: true,
				value(context, collection, iterator, customContext) {
					if (!_.isUndefined(customContext)) {
						context = customContext;
					}

					if (_.isFunction(iterator)) {
						iterator = iterator.bind(context);
					}

					return _.filter(collection, iterator);
				},
			},

			/**
			 * @method Utils.find
			 * @see lodash.find
			 */
			find: {
				public: true,
				value(context, collection, iterator, customContext) {
					if (!_.isUndefined(customContext)) {
						context = customContext;
					}

					if (_.isFunction(iterator)) {
						iterator = iterator.bind(context);
					}

					return _.find(collection, iterator);
				},
			},

			findWhere: {
				static: true,
				value() {
					throw new Error(`Error: Utils.findWhere has been replaced:\n
					Use Utils.find(collection, query<Object>) shorthand instead.`);
				},
			},

			/**
			 * @method Utils.first
			 * @see lodash.first
			 */
			first: {
				static: true,
				public: true,
				value: _.first,
			},

			/**
			 * @method Utils.flatten
			 * @see lodash.flatten
			 */
			flatten: {
				static: true,
				public: true,
				value: _.flatten,
			},

			/**
			 * Formats a template string with the given arguments.
			 * Each argument is matched to the template string by name.
			 * eg. formatUrl('http://www.example.com/{id}', { id: 1 }) => 'http://www.example.com/1'
			 * NOTE: Adapted from https://stackoverflow.com/a/18234317
			 * @method Utils.formatString
			 * @param {String} format The template string to format
			 * @param {Object} args The arguments to use for formatting
			 * @return {String} The formatted string
			 */
			formatString: {
				static: true,
				public: true,
				value: function (format, ...args) {
					let str = format;

					if (args.length) {
						const t = typeof args[0];
						const params = t === `string` || t === `number` ? Array.prototype.slice.call(args) : args[0];

						_.each(params, (value, key) => {
							str = str.replace(new RegExp(`\\{${key}\\}`, `gi`), value);
						});
					}

					return str.replace(/\/+/g, `/`); // remove any extra '/'
				},
			},

			/**
			 * @method Utils.generateGuid
			 */
			generateGuid: {
				public: true,
				static: true,
				value() {
					// Not guaranteed to be unique!
					const S4 = () => (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
					const block1 = S4() + S4();
					const block2 = S4();
					const block3 = S4().substr(0, 3);
					const block4 = S4();
					const block5 = S4() + S4() + S4();
					const result = `${block1}-${block2}-4${block3}-${block4}-${block5}`;
					return result.toLowerCase();
				},
			},

			/**
			 * @method Utils.getPropertyPath
			 * @see lodash.getPropertyPath
			 */
			getPropertyPath: {
				static: true,
				public: true,
				value(collection, propertyPath, defaultValue) {
					let propertyArray;
					let value;

					if (_.isString(propertyPath, 1)) {
						propertyArray = propertyPath.split(`.`);
					} else if (_.isArray(propertyPath)) {
						propertyArray = propertyPath;
					} else {
						throw new Error(`Utils.getPropertyPath: propertyPath must be a string or array`);
					}

					value = _.reduce(
						propertyArray,
						(obj, property) => {
							return _.isObject(obj) ? obj[property] : undefined;
						},
						collection
					);

					return _.isUndefined(value) ? defaultValue : value;
				},
			},

			/**
			 * @method Utils.groupBy
			 * @see lodash.groupBy
			 */
			groupBy: {
				public: true,
				value(context, collection, iterator, customContext) {
					if (!_.isUndefined(customContext)) {
						context = customContext;
					}

					if (_.isFunction(iterator)) {
						iterator = iterator.bind(context);
					}

					return _.groupBy(collection, iterator);
				},
			},

			/**
			 * @method Utils.includes
			 * @see lodash.includes
			 */
			includes: {
				static: true,
				public: true,
				value: _.includes,
			},

			/**
			 * @method Utils.indexBy
			 * @see lodash.indexBy
			 */
			indexBy: {
				public: true,
				value(context, collection, iterator, customContext) {
					if (!_.isUndefined(customContext)) {
						context = customContext;
					}

					if (_.isFunction(iterator)) {
						iterator = iterator.bind(context);
					}

					return _.indexBy(collection, iterator);
				},
			},

			/**
			 * @method Utils.indexOf
			 * @see lodash.indexOf
			 */
			indexOf: {
				static: true,
				public: true,
				value: _.indexOf,
			},

			/**
			 * @method Utils.initial
			 * @see lodash.initial
			 */
			initial: {
				static: true,
				public: true,
				value: _.initial,
			},

			/**
			 * @method Utils.insertAt
			 */
			insertAt: {
				static: true,
				public: true,
				value(str, index, value) {
					return str.substring(0, index) + value + str.substring(index);
				},
			},

			/**
			 * @method Utils.intersection
			 * @see lodash.intersection
			 */
			intersection: {
				static: true,
				public: true,
				value: _.intersection,
			},

			/**
			 * @method Utils.isArguments
			 * @see lodash.isArguments
			 */
			isArguments: {
				static: true,
				public: true,
				value: _.isArguments,
			},

			/**
			 * @method Utils.isArray
			 * @see lodash.isArray
			 */
			isArray: {
				static: true,
				public: true,
				value(arg, minLength) {
					if (_.isArray(arg)) {
						if (_.isUndefined(minLength) || arg.length >= minLength) {
							return true;
						}
					}

					return false;
				},
			},

			/**
			 * @method Utils.isBoolean
			 * @see lodash.isBoolean
			 */
			isBoolean: {
				static: true,
				public: true,
				value: _.isBoolean,
			},

			/**
			 * @method Utils.isDate
			 * @see lodash.isDate
			 */
			isDate: {
				static: true,
				public: true,
				value(arg) {
					return _.isDate(arg) && _.isFinite(arg.getTime());
				},
			},

			/**
			 * @method Utils.isDefined
			 * @see lodash.isDefined
			 */
			isDefined: {
				static: true,
				public: true,
				value(arg) {
					return !_.isUndefined(arg);
				},
			},

			/**
			 * @method Utils.isElement
			 * @param {*} subject - The value being tested.
			 * @returns {boolean} Whether the value is a DOMElement.
			 */
			isElement: {
				static: true,
				public: true,
				value: _.isElement,
			},

			/**
			 * @method Utils.isEmpty
			 * @see lodash.isEmpty
			 */
			isEmpty: {
				static: true,
				public: true,
				value: _.isEmpty,
			},

			/**
			 * @method Utils.isEqual
			 * @see lodash.isEqual
			 */
			isEqual: {
				static: true,
				public: true,
				value: _.isEqual,
			},

			/**
			 * @method Utils.isFinite
			 * @see lodash.isFinite
			 */
			isFinite: {
				static: true,
				public: true,
				value(arg, min) {
					if (_.isNumber(arg) && _.isFinite(arg)) {
						if (_.isUndefined(min) || arg >= min) {
							return true;
						}
					}

					return false;
				},
			},

			/**
			 * @method Utils.isFunction
			 * @param {*} subject - The value being tested.
			 * @returns {boolean} Whether the value is a function.
			 */
			isFunction: {
				static: true,
				public: true,
				value: _.isFunction,
			},

			/**
			 * @method Utils.isInt
			 * @see lodash.isInteger
			 * @param {*} subject - The value being tested
			 * @param {integer}
			 */
			isInt: {
				static: true,
				public: true,
				value(arg, min) {
					if (_.isInteger(arg)) {
						return !(_.isInteger(min) && arg < min);
					}

					return false;
				},
			},

			isNull: {
				static: true,
				public: true,
				value: _.isNull,
			},

			/**
			 * @method Utils.isObject
			 * @see lodash.isObject
			 * @param {*} subject - The value being tested
			 * @returns {boolean}
			 */
			isObject: {
				static: true,
				public: true,
				value: _.isObject,
			},

			/**
			 * @method Utils.isPromise
			 * @param {*} subject - The value being tested
			 * @returns {boolean} Whether the value is a promise.
			 */
			isPromise: {
				static: true,
				public: true,
				value(subject) {
					return _.isObject(subject) && _.isFunction(subject.then);
				},
			},

			/**
			 * @method Utils.isString
			 * @see lodash.isString
			 * @param {*} subject - The value being tested
			 * @returns {boolean}
			 */
			isString: {
				static: true,
				public: true,
				value(arg, minLength) {
					if (lodashIsString(arg)) {
						if (lodashIsUndefined(minLength) || arg.length >= minLength) {
							return true;
						}
					}

					return false;
				},
			},

			/**
			 * @method Utils.isUndefined
			 * @see lodash.isUndefined
			 * @param {*} subject - The value being tested
			 * @returns {boolean}
			 */
			isUndefined: {
				static: true,
				public: true,
				value: _.isUndefined,
			},

			/**
			 * @method Utils.keys
			 * @see lodash.keys
			 */
			keys: {
				static: true,
				public: true,
				value: _.keys,
			},

			/**
			 * @method Utils.last
			 * @see lodash.last
			 */
			last: {
				static: true,
				public: true,
				value: _.last,
			},

			/**
			 * @method Utils.lerp - Linearly interpolates between two points.
			 * @param {number} a - The start value.
			 * @param {number} b - The end value.
			 * @param {number} alpha - The alpha value between 0 and 1.
			 */
			lerp: {
				static: true,
				public: true,
				value(a, b, alpha) {
					const clippedAlpha = Math.max(Math.min(alpha, 1), 0);
					return a + (b - a) * clippedAlpha;
				},
			},

			/**
			 * @method Utils.map
			 * @see lodash.map
			 */
			map: {
				public: true,
				value(context, collection, iterator, customContext) {
					if (!_.isUndefined(customContext)) {
						context = customContext;
					}

					if (_.isFunction(iterator)) {
						iterator = iterator.bind(context);
					}

					return _.map(collection, iterator);
				},
			},

			/**
			 * @method Utils.max
			 * @see lodash.max
			 */
			max: {
				static: true,
				public: true,
				value: _.max,
			},

			/**
			 * @method Utils.min
			 * @see lodash.min
			 */
			min: {
				static: true,
				public: true,
				value: _.min,
			},

			/**
			 * @method Utils.omit
			 * @see lodash.omit
			 */
			omit: {
				static: true,
				public: true,
				value: _.omit,
			},

			/**
			 * @method Utils.parseInt
			 */
			parseInt: {
				static: true,
				public: true,
				value(integer) {
					return parseInt(integer, 10);
				},
			},

			pluck: {
				public: true,
				static: true,
				value() {
					throw new Error(`Error: Utils.pluck has been replaced:\n
					Use Utils.map(collection, propName<String>) shorthand instead.`);
				},
			},

			/**
			 * @method Utils.random
			 * @see lodash.random
			 */
			random: {
				static: true,
				public: true,
				value: _.random,
			},

			/**
			 * @method Utils.range
			 * @see lodash.range
			 */
			range: {
				static: true,
				public: true,
				value: _.range,
			},

			/**
			 * @method Utils.reduce
			 * @see lodash.reduce
			 */
			reduce: {
				public: true,
				value(context, collection, iterator, memo, customContext) {
					if (!_.isUndefined(customContext)) {
						context = customContext;
					}

					if (_.isFunction(iterator)) {
						iterator = iterator.bind(context);
					}

					return _.reduce(collection, iterator, memo);
				},
			},

			/**
			 * @method Utils.rest
			 * @see lodash.rest
			 */
			rest: {
				static: true,
				public: true,
				value: _.rest,
			},

			/**
			 * @method Utils.sample
			 * @see lodash.sample
			 */
			sample: {
				static: true,
				public: true,
				value: _.sample,
			},

			/**
			 * @method Utils#setPropertyPath
			 */
			setPropertyPath: {
				public: true,
				value(context, collection, propertyPath, value) {
					var propertyArray;

					if (this.isString(propertyPath, 1)) {
						propertyArray = propertyPath.split(`.`);
					} else if (this.isArray(propertyPath)) {
						propertyArray = propertyPath;
					} else {
						throw new Error(`Utils.setPropertyPath: propertyPath must be a string or array`);
					}

					this.reduce(
						propertyArray,
						(obj, property, index) => {
							if (index + 1 === propertyArray.length) {
								// end of the path, set the value
								obj[property] = value;
							} else {
								if (!this.isObject(obj[property])) {
									// overwrite if not an object
									obj[property] = {};
								}

								return obj[property];
							}
						},
						collection
					);
				},
			},

			/**
			 * @method Utils#some
			 * @see lodash#some
			 */
			some: {
				public: true,
				value(context, collection, iterator, customContext) {
					if (!_.isUndefined(customContext)) {
						context = customContext;
					}

					if (_.isFunction(iterator)) {
						iterator = iterator.bind(context);
					}

					return _.some(collection, iterator);
				},
			},

			/**
			 * @method Utils#sortBy
			 * @see lodash#sortBy
			 */
			sortBy: {
				public: true,
				value(context, collection, iterator, customContext) {
					if (!_.isUndefined(customContext)) {
						context = customContext;
					}

					if (_.isFunction(iterator)) {
						iterator = iterator.bind(context);
					}

					return _.sortBy(collection, iterator);
				},
			},

			/**
			 * @method Utils.strEqualsIgnoreCase
			 * @see lodash.strEqualsIgnoreCase
			 */
			strEqualsIgnoreCase: {
				static: true,
				public: true,
				value(str1, str2) {
					if (str1 == null || str2 == null) {
						// null or undefined
						return false;
					}

					if (str1.length !== str2.length) {
						return false;
					}

					return str1.toLowerCase() === str2.toLowerCase();
				},
			},

			/**
			 * @method Utils.throttle
			 * @see lodash.throttle
			 */
			throttle: {
				static: true,
				public: true,
				value: _.throttle,
			},

			/**
			 * @method Utils.toArray
			 * @see lodash.toArray
			 */
			toArray: {
				static: true,
				public: true,
				value: _.toArray,
			},

			/**
			 * @method Utils.trim
			 * @see lodash.trim
			 */
			trim: {
				static: true,
				public: true,
				value: _.trim,
			},

			/**
			 * @method Utils.union
			 * @see lodash.union
			 */
			union: {
				static: true,
				public: true,
				value: _.union,
			},

			/**
			 * @method Utils.unique
			 * @see lodash.uniq
			 */
			unique: {
				static: true,
				public: true,
				value: _.uniq,
			},

			/**
			 * @method Utils.uniqueId
			 * @see lodash.uniqueId
			 */
			uniqueId: {
				static: true,
				public: true,
				value: _.uniqueId,
			},

			/**
			 * @method Utils.without
			 * @see lodash.without
			 */
			without: {
				static: true,
				public: true,
				value: _.without,
			},
		}, // end "api"
	});
});

//# sourceMappingURL=Utils.js.map